import { Button, Popover, Text } from "@shopify/polaris"
import { ChatIcon, EmailIcon, TeamIcon } from "@shopify/polaris-icons"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useIntercom } from "react-use-intercom"

import SlackIcon from "@/assets/svg/slack-icon.svg"
import WechatIcon from "@/assets/svg/wechat-icon.svg"
import WhatsAppIcon from "@/assets/svg/whatsapp-icon.svg"
import { LINK_BOOK_A_DEMO, LINK_TRACKING_WHATSAPP } from "@/consts/link"
import { CROSS_SELL_POINT_ENUM } from "@/enums/CrossSellPoint"
import stores from "@/stores"
import { useSvgIcon } from "@/utils/components"
import { openLink } from "@/utils/redirectShopify"

import { useLeaveContactModal } from "../../DGSetting/components/LeaveContectModal"

const GetInTouch = () => {
  const { t: t2 } = useTranslation("delivery_guarantee")
  const { showNewMessage: intercomShowNewMessages } = useIntercom()
  const { DeliveryGuarantee } = stores
  const [popoverActive, setPopoverActive] = useState(false)
  const {
    modalComponent: LeaveContactModal,
    showModal: showLeaveContactModal,
  } = useLeaveContactModal()

  // 固定配置
  const hasBookDemo = true
  const utm = "pp-insurance-talk"
  const CROSS_SELL_POINT = CROSS_SELL_POINT_ENUM.PPIHelpCenterContact
  const isShowTalk = DeliveryGuarantee.infoData?.isShowTalk

  useEffect(() => {
    if (popoverActive) {
      window.CrossSellTracker?.logViewed(
        CROSS_SELL_POINT_ENUM.PPIHelpCenterContact,
      )
    }
  }, [popoverActive])

  // WeChat Work ID 配置
  const WECOM_IDS = {
    default: "cawcdeed8df7777ff4",
    "T-onboarding": "cawcde72cf688c044d",
    "pp-insurance-talk": "cawcdef3f59f7e868f",
  } as const

  // 构建WeCom链接的函数
  const buildWeComUrl = (wecomID: string, channel: string) => {
    return `https://work.weixin.qq.com/ca/${wecomID}?customer_channel=${channel}`
  }

  const CrossSellAction = [
    {
      content: "WhatsApp",
      icon: useSvgIcon(WhatsAppIcon),
      onAction: () => {
        setTimeout(() => {
          setPopoverActive(false)
        })
        const tracker = window.CrossSellTracker

        if (tracker?.redirectToWhatsApp && CROSS_SELL_POINT) {
          tracker.redirectToWhatsApp(
            CROSS_SELL_POINT,
            LINK_TRACKING_WHATSAPP,
          )
        } else {
          openLink(LINK_TRACKING_WHATSAPP)
        }
      },
    },
    {
      content: "Slack",
      icon: useSvgIcon(SlackIcon),
      onAction: () => {
        setTimeout(() => {
          setPopoverActive(false)
        })
        const tracker = window.CrossSellTracker

        if (tracker?.redirectToSlack && CROSS_SELL_POINT) {
          tracker.redirectToSlack(
            CROSS_SELL_POINT,
            `https://join.slack.com/t/parcelwill-community/shared_invite/zt-2zol5x0o6-eIat3ZbDPG1_ENuVd5LfDA?utm_source=${utm}`,
          )
        } else {
          openLink(
            `https://join.slack.com/t/parcelwill-community/shared_invite/zt-2zol5x0o6-eIat3ZbDPG1_ENuVd5LfDA?utm_source=${utm}`,
          )
        }
      },
    },
    {
      content: "WeCom",
      icon: useSvgIcon(WechatIcon),
      onAction: () => {
        setTimeout(() => {
          setPopoverActive(false)
        })
        const tracker = window.CrossSellTracker
        const wecomID =
          WECOM_IDS[utm as keyof typeof WECOM_IDS] || WECOM_IDS.default
        const wecomUrl = buildWeComUrl(wecomID, utm)

        if (tracker?.redirectToWeCom && CROSS_SELL_POINT) {
          tracker.redirectToWeCom(CROSS_SELL_POINT, wecomUrl)
        } else {
          openLink(wecomUrl)
        }
      },
    },
  ]

  type GetInTouchAction = {
    content: string;
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    onAction: () => void;
  };

  if (hasBookDemo) {
    CrossSellAction.unshift({
      content: "Demo",
      icon: () => <TeamIcon />,
      onAction: () => {
        setTimeout(() => {
          setPopoverActive(false)
        })
        const tracker = window.CrossSellTracker

        if (tracker?.redirectToDemo && CROSS_SELL_POINT) {
          tracker.redirectToDemo(CROSS_SELL_POINT, LINK_BOOK_A_DEMO)
        } else {
          openLink(LINK_BOOK_A_DEMO)
        }
      },
    })
  }

  const protectionContactActions: GetInTouchAction[] = [
    {
      content: t2("GetInTouch.SetUpEmail"),
      icon: EmailIcon,
      onAction: () => {
        showLeaveContactModal()
      },
    },
  ]

  const talkToExpertActions: GetInTouchAction[] = isShowTalk
    ? CrossSellAction.map((item) => ({
      content: item.content,
      icon: item.icon,
      onAction: item.onAction,
    }))
    : [
      {
        content: t2("GetInTouch.LiveChat"),
        icon: TeamIcon,
        onAction: () => {
          intercomShowNewMessages()
        },
      },
    ]

  return (
    <>
      <Popover
        active={popoverActive}
        activator={
          <div className="Polaris-ActionMenu-SecondaryAction">
            <Button
              icon={ChatIcon}
              disclosure
              variant="secondary"
              onClick={() => setPopoverActive(true)}
            >
              {t2("GetInTouch.TriggerLabel")}
            </Button>
          </div>
        }
        autofocusTarget="first-node"
        onClose={() => setPopoverActive(false)}
      >
        <div className="flex flex-col gap-1 p-1.5">
          <div className="pt-0.5 px-1.5 pb-1">
            <Text as="p" variant="headingSm">
              {t2("GetInTouch.ProtectionContact")}
            </Text>
          </div>
          {protectionContactActions.map((item) => (
            <div key={item.content} className="">
              <Button
                variant="tertiary"
                icon={item.icon}
                size="large"
                textAlign="left"
                fullWidth
                onClick={() => {
                  setPopoverActive(false)
                  item.onAction()
                }}
              >
                {item.content}
              </Button>
            </div>
          ))}
          <div className="pt-0.5 px-1.5 pb-1">
            <Text as="p" variant="headingSm">
              {t2("GetInTouch.TalkToExpert")}
            </Text>
          </div>
          {talkToExpertActions.map((item) => (
            <div key={item.content} className="">
              <Button
                variant="tertiary"
                icon={item.icon}
                size="large"
                textAlign="left"
                fullWidth
                onClick={() => {
                  setPopoverActive(false)
                  item.onAction()
                }}
              >
                {item.content}
              </Button>
            </div>
          ))}
        </div>
      </Popover>
      {LeaveContactModal}
    </>
  )
}

export { GetInTouch }
